import { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Stack,
  Checkbox,
  Textarea,
  Button,
  Group,
  Text,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconMessageCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useCreateDiscussion } from "../../hooks/useInternalDiscussions";
import {
  DiscussionCategory,
  discussionCategoryLabels,
  discussionCategoryDescriptions,
} from "../../services/internalDiscussions.service";
import { scrollToFirstError } from "../../utils/formValidation";

interface FlagDiscussionModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  workingDiagnosis?: string;
  /** Pre-selected category (e.g., when triggered from Discussion Required modal) */
  preselectedCategory?: DiscussionCategory;
}

interface FlagDiscussionFormValues {
  categories: DiscussionCategory[];
  otherText: string;
  initialComment: string;
}

/**
 * Modal for manually flagging a case for internal discussion.
 * Supports multi-selecting categories and adding a custom 'Other' reason.
 * Creates one internal discussion record per selected category.
 */
export function FlagDiscussionModal({
  opened,
  onClose,
  caseId,
  caseNumber,
  workingDiagnosis,
  preselectedCategory,
}: FlagDiscussionModalProps) {
  const createDiscussion = useCreateDiscussion();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FlagDiscussionFormValues>({
    initialValues: {
      categories: [],
      otherText: "",
      initialComment: "",
    },
    validate: {
      categories: (value) =>
        value.length === 0 ? "Please select at least one category" : null,
      otherText: (value, values) =>
        values.categories.includes(DiscussionCategory.OTHER) && !value.trim()
          ? "Please describe the discussion reason"
          : null,
    },
    validateInputOnBlur: true,
  });

  // Reset form when modal opens, and set preselected category if provided
  useEffect(() => {
    if (opened) {
      form.reset();
      setIsSubmitting(false);
      if (preselectedCategory) {
        form.setFieldValue("categories", [preselectedCategory]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, preselectedCategory]);

  const handleSubmit = form.onSubmit(
    async (values) => {
      setIsSubmitting(true);

      try {
        const results = await Promise.allSettled(
          values.categories.map((category) => {
            // When "Other" is selected, prepend the other reason to the initial comment
            // so it becomes the effective `reason` stored on the discussion record
            const effectiveComment =
              category === DiscussionCategory.OTHER
                ? [values.otherText.trim(), values.initialComment.trim()]
                    .filter(Boolean)
                    .join("\n\n")
                : values.initialComment.trim() || undefined;

            return createDiscussion.mutateAsync({
              caseId,
              category,
              initialComment: effectiveComment,
            });
          })
        );

        const allSucceeded = results.every((r) => r.status === "fulfilled");
        const someSucceeded = results.some((r) => r.status === "fulfilled");

        if (allSucceeded) {
          notifications.show({
            title: "Discussions Created",
            message: `${results.length} discussion${
              results.length > 1 ? "s" : ""
            } created successfully`,
            color: "green",
          });
          onClose();
        } else if (someSucceeded) {
          const failedCount = results.filter(
            (r) => r.status === "rejected"
          ).length;
          notifications.show({
            title: "Partial Success",
            message: `${failedCount} discussion${
              failedCount > 1 ? "s" : ""
            } failed to create. Please try flagging the remaining categories again.`,
            color: "orange",
          });
          // Update form to only include failed categories
          const failedCategories = values.categories.filter(
            (_, index) => results[index].status === "rejected"
          );
          form.setFieldValue("categories", failedCategories);
        } else {
          notifications.show({
            title: "Error",
            message: "All discussions failed to create. Please try again.",
            color: "red",
          });
        }
      } catch (err: any) {
        notifications.show({
          title: "Error",
          message: err.message || "Failed to create discussions",
          color: "red",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    () => scrollToFirstError(form)
  );

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconMessageCircle size={20} />
          <span>Flag for Internal Discussion</span>
        </Group>
      }
      size="lg"
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {/* Case Info */}
          <div>
            <Text size="sm" fw={500}>
              Case: {caseNumber}
            </Text>
            {workingDiagnosis && (
              <Text size="sm" c="dimmed" component="div">
                Your Working Diagnosis:{" "}
                {/* eslint-disable-next-line react/no-danger */}
                <span dangerouslySetInnerHTML={{ __html: workingDiagnosis }} />
              </Text>
            )}
          </div>

          <Divider />

          {/* Category Selection */}
          <Checkbox.Group
            label="Categories"
            withAsterisk
            {...form.getInputProps("categories")}
          >
            <Stack gap="sm" mt="xs">
              {Object.values(DiscussionCategory).map((category) => (
                <Checkbox
                  key={category}
                  value={category}
                  label={`${discussionCategoryLabels[category]} - ${discussionCategoryDescriptions[category]}`}
                  disabled={isSubmitting}
                />
              ))}
            </Stack>
          </Checkbox.Group>

          {form.values.categories.includes(DiscussionCategory.OTHER) && (
            <Textarea
              label="Please describe"
              placeholder="Describe the discussion reason..."
              withAsterisk
              minRows={2}
              maxRows={4}
              {...form.getInputProps("otherText")}
              disabled={isSubmitting}
            />
          )}

          <Divider />

          {/* Initial Comment */}
          <Textarea
            label="Initial Comment (optional)"
            placeholder="Add any additional context or specific questions..."
            minRows={3}
            maxRows={6}
            description="Comments are internal and not part of the final report."
            {...form.getInputProps("initialComment")}
            disabled={isSubmitting}
          />

          {/* Action Buttons */}
          <Group justify="space-between" mt="md">
            <Button
              variant="subtle"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              leftSection={<IconMessageCircle size={16} />}
            >
              Flag for Discussion
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
